import React, { useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import ClientForm from "@/components/clients/ClientForm";
import { CalendarPlus, ArrowLeft, Edit, Clock, Calendar } from "lucide-react";
import { formatCPF, formatCNPJ } from "@brazilian-utils/brazilian-utils";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { getClientById } from "@/services/ClientService";
import { getAppointments } from "@/services/AppointmentService";

export default function ClientDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const shouldOpenEditDialog = searchParams.get("edit") === "true";
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(shouldOpenEditDialog);

  const { data: clientResult, isLoading, refetch } = useQuery({
    queryKey: ['client', id],
    queryFn: () => getClientById(id!),
    enabled: !!id,
  });

  const client = clientResult?.data;

  const { data: appointmentsResult } = useQuery({
    queryKey: ['client-appointments', id],
    queryFn: () => getAppointments(false, undefined, id),
    enabled: !!id,
  });

  const appointments = appointmentsResult?.data || [];

  const handleEditDialogClose = () => {
    setIsEditDialogOpen(false);
    if (shouldOpenEditDialog) {
      navigate(`/dashboard/clients/${id}`);
    }
    refetch();
  };

  const formatDocument = (doc: string, type: string) => {
    if (type === 'cpf') {
      return formatCPF(doc);
    }
    return formatCNPJ(doc);
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="container mx-auto py-6 space-y-6">
          <div className="flex items-center space-x-4">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            <Skeleton className="h-8 w-[300px]" />
          </div>
          <Skeleton className="h-[400px] w-full" />
        </div>
      </DashboardLayout>
    );
  }

  if (!client) {
    return (
      <DashboardLayout>
        <div className="container mx-auto py-6">
          <div className="text-center py-12">
            <h2 className="text-2xl font-semibold mb-2">Cliente não encontrado</h2>
            <p className="text-gray-500 mb-6">O cliente que você está procurando não existe ou foi removido.</p>
            <Button asChild>
              <Link to="/dashboard/clients">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar para a lista de clientes
              </Link>
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Button variant="outline" size="sm" asChild>
              <Link to="/dashboard/clients">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Link>
            </Button>
            <h1 className="text-3xl font-bold">{client.name}</h1>
          </div>
          <div className="flex space-x-2">
            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Edit className="h-4 w-4 mr-2" />
                  Editar Cliente
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                  <DialogTitle>Editar Cliente</DialogTitle>
                </DialogHeader>
                <ClientForm client={client} onSuccess={handleEditDialogClose} />
              </DialogContent>
            </Dialog>
            <Button asChild>
              <Link to={`/dashboard/appointments?clientId=${client.id}`} className="flex items-center gap-2 bg-[#072a45] hover:bg-[#072a45] dark:bg-[#d4af37] dark:hover:bg-[#d4af37] text-white dark:text-[#072a45]">
                <CalendarPlus className="h-4 w-4 mr-2" />
                Novo Compromisso
              </Link>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="col-span-1">
            <CardHeader>
              <CardTitle>Informações Pessoais</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  {client.document_type === 'cpf' ? 'CPF' : 'CNPJ'}
                </p>
                <p>{formatDocument(client.document, client.document_type)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Tipo</p>
                <p>{client.document_type === 'cpf' ? 'Pessoa Física' : 'Pessoa Jurídica'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Email</p>
                <p>{client.email || "Não informado"}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Telefone</p>
                <p>{client.phone || "Não informado"}</p>
              </div>
              {client.address && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Endereço</p>
                  <p>
                    {client.address.street}, {client.address.number}
                    {client.address.complement && `, ${client.address.complement}`}
                  </p>
                  <p>
                    {client.address.neighborhood && `${client.address.neighborhood}, `}
                    {client.address.city} - {client.address.state}
                  </p>
                  <p>CEP: {client.address.zipCode}</p>
                </div>
              )}
              {client.notes && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Observações</p>
                  <p className="whitespace-pre-wrap">{client.notes}</p>
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-gray-500">Cadastrado em</p>
                <p>{format(new Date(client.created_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Última atualização</p>
                <p>{format(new Date(client.updated_at), "dd/MM/yyyy", { locale: ptBR })}</p>
              </div>
            </CardContent>
          </Card>

          <div className="col-span-1 lg:col-span-2">
            <Tabs defaultValue="appointments" className="w-full">
              <TabsList className="w-full">
                <TabsTrigger value="appointments" className="flex-1">
                  <Calendar className="h-4 w-4 mr-2" />
                  Compromissos
                </TabsTrigger>
              </TabsList>
              <TabsContent value="appointments" className="mt-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle>Compromissos Agendados</CardTitle>
                      <CardDescription>
                        Próximos compromissos com este cliente
                      </CardDescription>
                    </div>
                    <Button asChild>
                      <Link to={`/dashboard/appointments?clientId=${client.id}`}  className="flex items-center gap-2 bg-[#072a45] hover:bg-[#072a45] dark:bg-[#d4af37] dark:hover:bg-[#d4af37] text-white dark:text-[#072a45]">
                        <CalendarPlus className="h-4 w-4 mr-2" />
                        Agendar
                      </Link>
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {appointments && appointments.length > 0 ? (
                      <div className="space-y-4">
                        {appointments.map((appointment) => (
                          <div 
                            key={appointment.id} 
                            className="border rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800"
                          >
                            <div className="flex justify-between">
                              <h3 className="font-medium">{appointment.title}</h3>
                              <span className="text-sm bg-lawyer-primary/10 text-lawyer-primary px-2 py-1 rounded-full">
                                {appointment.type}
                              </span>
                            </div>
                            <div className="text-sm mt-2">
                              <span className="font-medium">Data: </span>
                              {format(new Date(appointment.date), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                            </div>
                            <div className="text-sm mt-1">
                              <span className="font-medium">Local: </span>
                              {appointment.location || "Não informado"}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        Nenhum compromisso agendado com este cliente.
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
